import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { DRAW_INTERVAL_MS, HISTORY_SIZE } from '../../../core/constants/lottery';
import { SESSION_KEYS } from '../../../core/constants/storage';
import { historyUpTo, resultForSlot, slotIndexAt, slotStartAt } from '../../../core/lottery/draw';
import { readSession, writeSession } from '../../../core/storage/session-store';

// App-wide draw clock. TSS vite/03 §"Cross-app server state" — this is the
// simulated stand-in for it: the store, the hero and "mis números" all read
// the same slot, so there is exactly one source of truth for "which draw are
// we in". See docs/decisions/0001-simulated-lottery-domain.md.

const TICK_MS = 1000;
const SEED_RANGE = 2 ** 31;

const DrawsContext = createContext(null);

// One seed per browser session drives both the pre-sold inventory and every
// draw result, so a reload rebuilds the identical session.
function readOrCreateSeed() {
    const stored = readSession(SESSION_KEYS.LOTTERY_SEED);
    if (typeof stored === 'number') return stored;

    const seed = Math.floor(Math.random() * SEED_RANGE);
    writeSession(SESSION_KEYS.LOTTERY_SEED, seed);
    return seed;
}

export function DrawsProvider({ children }) {
    const [seed] = useState(readOrCreateSeed);
    const [now, setNow] = useState(() => Date.now());

    // The demo cycle is a real 30 minutes. This offset fast-forwards the whole
    // timeline by whole slots so a viewer can watch buy → draw → win without
    // waiting, and history/countdown/tickets all stay coherent because every
    // one of them derives from the slot index.
    const [slotOffset, setSlotOffset] = useState(
        () => readSession(SESSION_KEYS.LOTTERY_SLOT_OFFSET, 0) ?? 0,
    );

    useEffect(() => {
        const timer = setInterval(() => setNow(Date.now()), TICK_MS);
        return () => clearInterval(timer);
    }, []);

    useEffect(() => {
        writeSession(SESSION_KEYS.LOTTERY_SLOT_OFFSET, slotOffset);
    }, [slotOffset]);

    const simulatedNow = now + slotOffset * DRAW_INTERVAL_MS;
    const currentSlot = slotIndexAt(simulatedNow);
    const nextDrawAt = slotStartAt(currentSlot + 1);

    const resultOf = useCallback((slotIndex) => resultForSlot(slotIndex, seed), [seed]);

    const latest = useMemo(() => resultOf(currentSlot), [resultOf, currentSlot]);
    const history = useMemo(() => historyUpTo(currentSlot, seed, HISTORY_SIZE), [currentSlot, seed]);

    const advanceDraw = useCallback(() => setSlotOffset(offset => offset + 1), []);

    const value = useMemo(() => ({
        seed,
        currentSlot,
        upcomingSlot: currentSlot + 1,
        latest,
        history,
        nextDrawAt,
        msToNextDraw: nextDrawAt - simulatedNow,
        resultOf,
        advanceDraw,
    }), [seed, currentSlot, latest, history, nextDrawAt, simulatedNow, resultOf, advanceDraw]);

    return <DrawsContext.Provider value={value}>{children}</DrawsContext.Provider>;
}

export function useDraws() {
    const ctx = useContext(DrawsContext);
    if (!ctx) throw new Error('useDraws must be used inside <DrawsProvider>');
    return ctx;
}
